import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { FiLock } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';

/**
 * Route guard for the admin area.
 *
 * This is a *convenience*, not a security boundary. It decides what to render;
 * it does not decide what data exists. Anyone can edit their stored user object
 * to say `role: "admin"` and reach this page — they will simply see an empty
 * dashboard, because every admin endpoint re-checks the role server-side
 * against the stored record. Never move an authorisation decision here.
 */
export default function RequireAdmin({ children }) {
  const { user, isAuthenticated, initialising } = useAuth();
  const location = useLocation();

  // Wait for a restored token to be revalidated before deciding — otherwise a
  // refresh on /admin bounces the admin to the login page for a moment.
  if (initialising) {
    return (
      <div className="mm-admin__boot">
        <Spinner animation="border" role="status" aria-label="Checking access" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (user?.role !== 'admin') {
    return (
      <div className="mm-admin__denied">
        <span className="mm-admin__denied-icon" aria-hidden="true">
          <FiLock size={26} />
        </span>
        <h1 className="mm-h2">Admin access only</h1>
        <p className="mm-lead">
          You&rsquo;re signed in as <strong>{user?.email}</strong>, which
          doesn&rsquo;t have permission to view the appointments dashboard.
        </p>
        <Link to="/" className="btn btn-primary px-4 mt-2">
          Back to site
        </Link>
      </div>
    );
  }

  return children;
}
